import { useState, useEffect, useCallback } from 'react';
import { apiService, AuthUser } from '../../services/apiService';

interface AdminAuthState {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
}

export function useAdminAuth() {
  const [state, setState] = useState<AdminAuthState>({
    user: null,
    loading: true,
    error: null,
  });

  const checkAuth = useCallback(async () => {
    try {
      const response = await apiService.authMe();
      const user = response.user;
      if (user && user.is_staff) {
        setState({ user, loading: false, error: null });
      } else {
        setState({ user: null, loading: false, error: null });
      }
    } catch {
      setState({ user: null, loading: false, error: null });
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (username: string, password: string) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const result = await apiService.authLogin(username, password);
      const user = result.user;
      if (user && user.is_staff) {
        setState({ user, loading: false, error: null });
        return true;
      } else {
        setState({ user: null, loading: false, error: 'Admin access required' });
        await apiService.authLogout();
        return false;
      }
    } catch (e: any) {
      setState({ user: null, loading: false, error: 'Invalid credentials' });
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiService.authLogout();
    } catch {
      // ignore
    }
    setState({ user: null, loading: false, error: null });
  }, []);

  return { ...state, login, logout };
}
